import type { MouseEventHandler, ReactElement } from 'react';
import type { ButtonProps } from '@mfw/components';

import {
  useInteractions,
  FloatingOverlay,
  FloatingPortal,
  type Placement,
  useFloating,
  autoUpdate,
  useDismiss,
  offset,
  shift,
  flip,
} from '@floating-ui/react';
import { createContextFactory, MotionDropdown, Button } from '@mfw/components';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import classNames from 'classnames';
import omit from 'lodash/omit';
import React from 'react';

import MenuItemButton from './MenuItemButton';
import MenuItem from './MenuItem';
import { useOverlayBackClose } from '../DialogBrowserBack';
import { List } from '../List';
import './Menu.scss';

export interface MenuProps extends Omit<ButtonProps, 'defaultValue'> {
  /** Placement of dropdown menu */
  placement?: Placement;
  onClose?: () => void;
  /**
   * Allows select of multiple values, otherwise if not provided menu is closed
   */
  multiple?: boolean;
  /**
   * Indicates that menu is a list which will use ListItem as a child
   */
  asList?: boolean;
}

/**
 * Represents the context for the Menu component.
 */
export type MenuContext = {
  /**
   * A function to set the open state of the Menu.
   * @param value - The new open state value.
   */
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;

  /**
   * A boolean indicating whether multiple items can be selected in the Menu.
   */
  multiple: boolean;

  /**
   * A boolean indicating whether the Menu is currently open.
   */
  open: boolean;
};

interface MenuComponent<TMenuProps> extends React.ForwardRefExoticComponent<
  Omit<MenuProps, 'ref'> & TMenuProps
> {
  MenuItemButton: typeof MenuItemButton;
  MenuItem: typeof MenuItem;
  displayName: string;
}

const MenuContext = createContextFactory({
  devToolsName: 'MenuContext',
  initialValue: {} as MenuContext,
});

const Menu = <TMenuProps extends MenuProps>(
  Component: React.ComponentType<TMenuProps>,
) => {
  function MenuDropdown(
    props: TMenuProps,
    ref: React.ForwardedRef<HTMLButtonElement | HTMLDivElement>,
  ) {
    const [actualPlacement, setActualPlacement] = React.useState<
      Placement | undefined
    >();
    const componentProps = omit(props, 'asList') as TMenuProps;
    const listItems: ReactElement[] = [];
    const otherItems: ReactElement[] = [];

    React.Children.forEach(
      props.children as React.ReactElement[],
      (child: React.ReactElement) => {
        if (!child) return;
        switch (child.type) {
          case MenuItemButton:
            listItems.push(child);
            break;
          case MenuItem:
            listItems.push(child);
            break;
          case Divider:
            listItems.push(child);
            break;
          default:
            otherItems.push(child);
        }
      },
    );
    const [open, setOpen] = React.useState(false);
    const [anchor, setAnchor] = React.useState<Element | null>(null);

    const backClose = useOverlayBackClose<HTMLDivElement>(open, () => {
      setOpen(false);
      if (props.onClose) props.onClose();
    });

    const { context, floatingStyles, refs } = useFloating({
      elements: {
        reference: anchor,
      },
      middleware: [offset(8), flip(), shift({ crossAxis: true })],
      onOpenChange: (isOpen: boolean) => {
        setOpen(isOpen);
        if (!isOpen && props.onClose) props.onClose();
      },
      open,
      placement: props.placement || 'bottom-end',
      whileElementsMounted: autoUpdate,
    });

    const handleDismiss = useDismiss(context);

    const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
      if (props.onClick) props.onClick(e);
      setOpen((state) => !state);
    };

    const useInteractionsArgs = React.useMemo(() => {
      return [handleDismiss];
    }, [handleDismiss]);

    const { getFloatingProps, getReferenceProps } =
      useInteractions(useInteractionsArgs);

    const styles = React.useMemo(() => {
      return {
        ...floatingStyles,
        minWidth: 220,
        width: refs.reference.current?.getBoundingClientRect().width,
      };
    }, [floatingStyles, refs.reference]);

    React.useEffect(() => {
      if (open) setActualPlacement(context.placement);
    }, [open, context.placement]);

    const providedValue = React.useMemo(() => {
      return {
        multiple: Boolean(props.multiple),
        open,
        setOpen,
      };
    }, [open, props.multiple, setOpen]);
    return (
      <MenuContext.Provider value={providedValue}>
        <Component
          {...componentProps}
          {...getReferenceProps()}
          ref={(e) => {
            setAnchor(e);
            if (typeof ref === 'function') {
              ref(e);
            } else {
              if (ref && ref.current === null) ref.current = e;
            }
          }}
          onClick={handleClick}
        >
          {otherItems}
        </Component>
        {open && (
          <FloatingOverlay lockScroll {...backClose}>
            <FloatingPortal>
              <div
                ref={(e: any) => {
                  refs.setFloating(e);
                }}
                className={classNames('Mfw-MenuList-root', props.className)}
                style={styles}
                {...getFloatingProps()}
              >
                {actualPlacement && (
                  <MotionDropdown placement={actualPlacement}>
                    <List
                      className={classNames('Mfw-MenuList-list', {
                        'Mfw-MenuList-list--asList': props.asList,
                      })}
                    >
                      {listItems}
                    </List>
                  </MotionDropdown>
                )}
              </div>
            </FloatingPortal>
          </FloatingOverlay>
        )}
      </MenuContext.Provider>
    );
  }

  return React.forwardRef(MenuDropdown) as MenuComponent<TMenuProps>;
};

export const ButtonMenu = Menu(Button as React.ComponentType<ButtonProps>);
export const IconButtonMenu = Menu(IconButton);

IconButtonMenu.MenuItemButton = MenuItemButton;
IconButtonMenu.MenuItem = MenuItem;

ButtonMenu.MenuItemButton = MenuItemButton;
ButtonMenu.MenuItem = MenuItem;

export function useMenuContext() {
  return MenuContext.useContext();
}

export { Menu };
export default Menu;
